import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Paper, Typography, Grid, TextField, Button, Autocomplete, Table, TableHead, TableRow, TableCell, TableBody, DialogTitle, DialogContent, DialogActions, IconButton, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel, CircularProgress, Tooltip, TableContainer, Stack } from "@mui/material";
import AppDialog from "../../../common/AppDialog";
import { UploadFile as UploadFileIcon, Image as ImageIcon, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon } from "@mui/icons-material";
import { deleteData, getData, postData, putData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import { getStatusColor } from "../../../utils/statusColor";

function ReimbursementsTab() {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const hrId = userData?.id;
    const fileInputRef = useRef(null);
    const [reimbursementEmployees, setReimbursementEmployees] = useState([]);
    const [claims, setClaims] = useState([]);
    const [upcomingMonths, setUpcomingMonths] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [claimType, setClaimType] = useState("");
    const [payrollCycle, setPayrollCycle] = useState("");
    const [requestedAmount, setRequestedAmount] = useState("");
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [openEdit, setOpenEdit] = useState(false);
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [approvedAmount, setApprovedAmount] = useState("");
    const [comment, setComment] = useState("");
    const [openPreview, setOpenPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewType, setPreviewType] = useState("");
    const [showNextFY, setShowNextFY] = useState(false);
    const [confirmMsg, setConfirmMsg] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const fetchReimbursementEmployees = useCallback(async () => {
        try {
            const res = await getData(API_ENDPOINTS.getPayrollsByHrId(hrId));
            const data = res?.result || [];
            const filtered = data.filter((emp) => emp.id !== null);
            setReimbursementEmployees(filtered);
        } catch (err) {
            return null;
        }
    }, [hrId]);

    const fetchUpcomingMonths = useCallback(async (includeNext = false) => {
        const res = await getData(API_ENDPOINTS.getUpcomingPayrollMonths(includeNext));
        if (res?.statusCode === 200) {
            const formatted = res.result.months.map((m) => {
                const [y, mo] = m.split("-");
                const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                return `${names[Number(mo) - 1]} ${y}`;
            });
            setUpcomingMonths(formatted);
        }
    }, []);

    const fetchReimbursements = useCallback(async () => {
        setTableLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.getReimbursementsByHrId(hrId));
            if (res?.statusCode === 200) {
                const flat = [];
                res.result.forEach((group) => {
                    group.reimbursements.forEach((item) => {
                        const emp = item.employee;
                        flat.push({
                            id: item.id,
                            employeeId: emp.emp_id,
                            employeeName: `${emp.first_name} ${emp.last_name}`,
                            claimType: item.claimType,
                            claimDate: new Date(item.createdAt).toLocaleDateString(),
                            requestedAmount: Number(item.postamount),
                            approvedAmount: Number(item.amount),
                            receiptUrl: item.receiptUrl,
                            status: item.status,
                            payrollCycle: item.payrollCycle || group.payrollCycle || "Not Assigned"
                        });
                    });
                });
                setClaims(flat);
            }
        } finally {
            setTableLoading(false);
        }
    }, [hrId]);

    useEffect(() => {
        fetchReimbursementEmployees();
        fetchUpcomingMonths(showNextFY);
        fetchReimbursements();
    }, [showNextFY, fetchReimbursementEmployees, fetchUpcomingMonths, fetchReimbursements]);

    // ================= FILE =================
    const MAX_FILE_SIZE_MB = 20;

    const handleFileChange = (e) => {
        const f = e.target.files[0];
        if (!f) return;

        if (f.type !== "application/pdf") {
            setSnackbar({ open: true, message: "Only PDF is allowed", severity: "error" });
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            setSnackbar({ open: true, message: `Size exceeds ${MAX_FILE_SIZE_MB}MB`, severity: "error" });
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setFile(f);
        setFilePreview(URL.createObjectURL(f));
    };

    const removeFile = () => {
        setFile(null);
        setFilePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // ================= CREATE =================
    const handleCreate = async () => {
        if (!selectedEmployee || !claimType || !payrollCycle || !requestedAmount || !file) {
            setSnackbar({ open: true, message: "Please fill all fields", severity: "error" });
            return;
        }

        setSubmitLoading(true);
        try {
            const fd = new FormData();
            fd.append("postamount", requestedAmount);
            fd.append("amount", requestedAmount);
            fd.append("employeePersonalId", selectedEmployee.employeePersonalId);
            fd.append("employeeName", `${selectedEmployee.first_name} ${selectedEmployee.last_name}`);
            fd.append("claimType", claimType);
            fd.append("payrollCycle", payrollCycle);
            fd.append("status", false);
            fd.append("hr_id", hrId);
            fd.append("receipt", file);

            const res = await postData(API_ENDPOINTS.createReimbursement, fd);
            if (res?.statusCode === 200) {
                setSnackbar({ open: true, message: "Claim submitted", severity: "success" });
                setShowCreate(false);
                setSelectedEmployee(null);
                setClaimType("");
                setPayrollCycle("");
                setRequestedAmount("");
                removeFile();
                fetchReimbursements();
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    // ================= APPROVE =================
    const openEditDialog = (row) => {
        if (row.status) return;
        setSelectedClaim(row);
        setApprovedAmount(row.approvedAmount || "");
        setPayrollCycle(row.payrollCycle || "");
        setComment("");
        setOpenEdit(true);
    };

    const handleApprove = async (isFinalApprove = true) => {
        if (Number(approvedAmount) < 0 || Number(approvedAmount) > selectedClaim.requestedAmount) {
            setSnackbar({ open: true, message: "Approved amount must be between 0 and requested amount", severity: "error" });
            return;
        }
        if (!payrollCycle) {
            setSnackbar({ open: true, message: "Please select a Payroll Cycle", severity: "error" });
            return;
        }

        const payload = {
            id: selectedClaim.id,
            comment,
            status: isFinalApprove,
            amount: approvedAmount || 0,
            payrollCycle: payrollCycle,
        };

        try {
            setSubmitLoading(true);
            const res = await putData(API_ENDPOINTS.updateReimbursementById, payload);
            if (res?.statusCode === 200) {
                setSnackbar({ open: true, message: isFinalApprove ? "Approved successfully" : "Changes saved", severity: "success" });
                setOpenEdit(false);
                fetchReimbursements();
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDelete = async (row) => {
        if (row.status) return;
        await deleteData(API_ENDPOINTS.deleteReimbursementById(row.id));
        fetchReimbursements();
    };

    return (
        <Box>
            <Stack
                direction="row"
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3 // Spacing below the header
                }}
            >
                <Typography variant="h5" fontWeight={600}>
                    HR Reimbursements
                </Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={showCreate}
                            onChange={(e) => setShowCreate(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Submit claim on behalf of employee"
                    // Applying a smaller font size to the label for a cleaner look
                    componentsProps={{ typography: { variant: 'body2', fontWeight: 500 } }}
                />
            </Stack>

            {showCreate && (
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={reimbursementEmployees}
                                value={selectedEmployee}
                                onChange={(e, v) => setSelectedEmployee(v)}
                                getOptionLabel={(o) => `${o.first_name} ${o.last_name} (${o.emp_id})`}
                                size="small"
                                renderInput={(params) => <TextField {...params} label="Employee" size="small" />}
                                filterSelectedOptions
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="claim-type-label">Claim Type</InputLabel>
                                <Select
                                    labelId="claim-type-label"
                                    label="Claim Type"
                                    value={claimType}
                                    onChange={(e) => setClaimType(e.target.value)}
                                >
                                    <MenuItem value="Medical">Medical</MenuItem>
                                    <MenuItem value="Travel">Travel</MenuItem>
                                    <MenuItem value="Internet">Internet</MenuItem>
                                    <MenuItem value="Fuel">Fuel</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="payroll-cycle-label">Payroll Cycle</InputLabel>
                                <Select
                                    labelId="payroll-cycle-label"
                                    label="Payroll Cycle"
                                    value={payrollCycle}
                                    onChange={(e) => {
                                        if (e.target.value === "ADD_NEXT_FY") {
                                            setConfirmMsg("This will load months for the next financial year. Do you want to proceed?");
                                        } else {
                                            setPayrollCycle(e.target.value);
                                        }
                                    }}
                                >
                                    {upcomingMonths.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                    {!showNextFY && (
                                        <MenuItem value="ADD_NEXT_FY" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                            + Add Next Financial Year
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Requested Amount"
                                type="number"
                                value={requestedAmount}
                                onChange={(e) => setRequestedAmount(e.target.value)}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ textTransform: 'none' }}>
                                    Upload Receipt
                                    <input hidden type="file" accept="application/pdf" ref={fileInputRef} onChange={handleFileChange} />
                                </Button>
                                <Typography variant="caption" color="text.secondary">PDF Only · Max {MAX_FILE_SIZE_MB}MB</Typography>
                            </Box>
                        </Grid>
                        {filePreview && (
                            <Grid size={{ xs: 12 }}>
                                <Paper sx={{ width: 240, p: 1, position: "relative" }}>
                                    <IconButton size="small" sx={{ position: "absolute", top: 2, right: 2 }} onClick={removeFile}><CloseIcon fontSize="small" /></IconButton>
                                    <embed src={filePreview} type="application/pdf" width="100%" />
                                </Paper>
                            </Grid>
                        )}
                        <Grid size={{ xs: 12 }}>
                            <Button variant="contained" onClick={handleCreate} disabled={submitLoading}>
                                {submitLoading ? <CircularProgress size={22} /> : "Submit Claim"}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Table sx={{ minWidth: 900 }}>
                    <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee ID</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee Name</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Claim Type</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Claim Date</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Cycle</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="right">Requested</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="right">Approved</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="center">Status</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="center">Proof</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, minWidth: 100 }} align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                        {tableLoading ? (
                            <TableRow><TableCell colSpan={10} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : claims.length === 0 ? (
                            <TableRow><TableCell colSpan={10} align="center">No claims found</TableCell></TableRow>
                        ) : (
                            claims.map((row) => {
                                // Map boolean/string status to getStatusColor keys
                                const statusKey = row.status ? "Approved" : "Pending";
                                const colors = getStatusColor(statusKey);

                                return (
                                    <TableRow key={row.id} hover sx={{
                                        '&:hover': {
                                            bgcolor: '#F1F5F9 !important'
                                        }
                                    }}>
                                        <TableCell>{row.employeeId}</TableCell>
                                        <TableCell>{row.employeeName}</TableCell>
                                        <TableCell>{row.claimType}</TableCell>
                                        <TableCell>{row.claimDate}</TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: row.payrollCycle === "Not Assigned" ? 400 : 700,
                                                    color: row.payrollCycle === "Not Assigned" ? "orange" : "primary.main",
                                                    fontStyle: row.payrollCycle === "Not Assigned" ? "italic" : "normal"
                                                }}
                                            >
                                                {row.payrollCycle}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">₹{row.requestedAmount?.toLocaleString()}</TableCell>
                                        <TableCell align="right">₹{row.approvedAmount?.toLocaleString()}</TableCell>

                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: "inline-block",
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 10,
                                                    fontSize: "0.75rem",
                                                    fontWeight: "bold",
                                                    bgcolor: colors.bg,
                                                    color: colors.color,
                                                }}
                                            >
                                                {statusKey}
                                            </Box>
                                        </TableCell>

                                        <TableCell align="center">
                                            {row.receiptUrl && (
                                                <IconButton
                                                    onClick={() => {
                                                        setPreviewUrl(row.receiptUrl);
                                                        setPreviewType(row.receiptUrl.toLowerCase().endsWith(".pdf") ? "pdf" : "img");
                                                        setOpenPreview(true);
                                                    }}
                                                >
                                                    <ImageIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>

                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                <Tooltip title={row.status ? "Already approved" : "Approve"}>
                                                    <span>
                                                        <IconButton disabled={row.status} onClick={() => openEditDialog(row)} size="small">
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={row.status ? "Cannot delete approved claim" : "Delete"}>
                                                    <span>
                                                        <IconButton disabled={row.status} onClick={() => handleDelete(row)} size="small">
                                                            <DeleteIcon fontSize="small" color={row.status ? "disabled" : "error"} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* APPROVE DIALOG */}
            <AppDialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", bgcolor: 'primary.main', color: 'white' }}>
                    Verify & Assign Cycle
                    <IconButton onClick={() => setOpenEdit(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <TextField label="Requested Amount" value={`₹ ${selectedClaim?.requestedAmount || 0}`} InputProps={{ readOnly: true }} fullWidth sx={{ mb: 2, backgroundColor: "#f5f7fa" }} />
                    <TextField
                        label="Approved Amount"
                        type="number"
                        value={approvedAmount}
                        onChange={(e) => setApprovedAmount(e.target.value)}
                        fullWidth
                        sx={{ mb: 2 }}
                        error={Number(approvedAmount) > (selectedClaim?.requestedAmount || 0)}
                        helperText={Number(approvedAmount) > (selectedClaim?.requestedAmount || 0) ? `Amount cannot exceed requested ₹${selectedClaim?.requestedAmount}` : ""}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Payroll Cycle (Payout Period)</InputLabel>
                        <Select
                            value={payrollCycle}
                            onChange={(e) => {
                                if (e.target.value === "ADD_NEXT_FY") {
                                    setConfirmMsg("This will load months for the next financial year. Do you want to proceed?");
                                } else {
                                    setPayrollCycle(e.target.value);
                                }
                            }}
                            label="Payroll Cycle (Payout Period)"
                        >
                            {upcomingMonths.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                            {!showNextFY && (
                                <MenuItem value="ADD_NEXT_FY" sx={{ color: 'primary.main', fontWeight: 600 }}>+ Add Next Financial Year</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <TextField label="Comment" multiline rows={3} value={comment} onChange={(e) => setComment(e.target.value)} fullWidth />
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                    <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
                    <Button variant="outlined" onClick={() => handleApprove(false)} disabled={submitLoading || !payrollCycle}>Save Changes</Button>
                    <Button
                        variant="contained"
                        onClick={() => handleApprove(true)}
                        disabled={submitLoading || Number(approvedAmount) <= 0 || Number(approvedAmount) > (selectedClaim?.requestedAmount || 0) || !payrollCycle}
                    >
                        {submitLoading ? <CircularProgress size={20} /> : "Approve & Payout"}
                    </Button>
                </DialogActions>
            </AppDialog>

            {/* PREVIEW */}
            <AppDialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
                    Receipt Preview
                    <IconButton onClick={() => setOpenPreview(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    {previewType === "pdf" ? (
                        <embed src={previewUrl} type="application/pdf" width="100%" height="500px" />
                    ) : (
                        <img src={previewUrl} alt="receipt" style={{ width: "100%" }} />
                    )}
                </DialogContent>
            </AppDialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>

            <AppDialog open={!!confirmMsg} onClose={() => setConfirmMsg(null)}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent><Typography>{confirmMsg}</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmMsg(null)}>Cancel</Button>
                    <Button variant="contained" onClick={() => { setShowNextFY(true); setConfirmMsg(null); }}>Load Next FY</Button>
                </DialogActions>
            </AppDialog>
        </Box>
    );
}

export default ReimbursementsTab;